import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as AvailableIcon,
  DoNotDisturb as BusyIcon,
  Cancel as OfflineIcon,
  Star as StarIcon,
  Block as BlockIcon,
  CheckCircleOutline as ActivateIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import {
  deleteDriver,
  toggleDriverActive
} from '../../redux/slices/driversSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  let icon;
  
  switch (status) {
    case 'available':
      color = 'warning'; // Gelb für verfügbar
      icon = <AvailableIcon />;
      break;
    case 'busy':
      color = 'error'; // Rot für besetzt
      icon = <BusyIcon />;
      break;
    case 'offline':
    default:
      color = 'default'; // Grau für offline
      icon = <OfflineIcon />;
      break;
  }
  
  return {
    color: status === 'offline' ? 'inherit' : 'white',
    backgroundColor: status === 'offline' ? undefined : theme.palette[color].main,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

const RatingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  '& .MuiSvgIcon-root': {
    color: theme.palette.warning.main,
    fontSize: '1.2rem',
  },
}));

// Statische Maps – einmalig erstellt, nie neu berechnet
const STATUS_LABEL_MAP = {
  'available': 'Verfügbar',
  'busy': 'Besetzt',
  'offline': 'Nicht verfügbar',
};

const STATUS_PRIORITY = { 'available': 2, 'busy': 1, 'offline': 0 };

const DriversList = () => {
  const dispatch = useDispatch();
  // Verwende neues AppData System statt altes drivers slice (memoized)
  const drivers = useSelector(state => state.appData.drivers);
  const isLoading = useSelector(state => state.appData.isLoading.drivers);
  const error = useSelector(state => state.appData.error.drivers);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active');
  const [sortBy, setSortBy] = useState('name');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDriver, setMenuDriver] = useState(null);
  
  // Flag, um zu verhindern, dass useEffect mehrmals ausgeführt wird
  const [dataLoaded, setDataLoaded] = useState(false);

  // AppDataManager stellt drivers bereit – kein manueller Fetch nötig.
  // dataLoaded wird nur gesetzt um isLoading-State sauber zu halten.
  useEffect(() => {
    if (drivers && drivers.length >= 0) {
      setDataLoaded(true);
    }
  }, [drivers]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  }, []);

  const handleActiveFilterChange = useCallback((event) => {
    setActiveFilter(event.target.value);
    setPage(0);
  }, []);

  const handleSortByChange = useCallback((event) => {
    setSortBy(event.target.value);
    setPage(0);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuDriver(null);
  }, []);

  const handleOpenMenu = useCallback((event, driver) => {
    setMenuAnchor(event.currentTarget);
    setMenuDriver(driver);
  }, []);

  const handleDeleteClick = useCallback((driver) => {
    setSelectedDriver(driver);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
    setMenuDriver(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedDriver) {
      dispatch(deleteDriver(selectedDriver.id));
      setDeleteDialogOpen(false);
      setSelectedDriver(null);
    }
  }, [dispatch, selectedDriver]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedDriver(null);
  }, []);

  const handleToggleActive = useCallback((driver) => {
    dispatch(toggleDriverActive({ id: driver.id, isActive: driver.isActive === false }));
    setMenuAnchor(null);
    setMenuDriver(null);
  }, [dispatch]);

  const translateStatus = useCallback((status) => STATUS_LABEL_MAP[status] || status, []);

  // Filter und Sort – wird nur neu berechnet wenn drivers oder Filterkriterien sich ändern.
  // Echtzeit-Updates vom AppDataManager triggern automatisch eine Neuberechnung,
  // da `drivers` als Dependency enthalten ist.
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return drivers.filter(driver => {
      const searchMatch =
        driver.name?.toLowerCase().includes(term) ||
        driver.phone?.includes(searchTerm);
      const statusMatch =
        statusFilter === 'all' ||
        (statusFilter === 'available' && driver.isAvailable) ||
        (statusFilter === 'busy' && !driver.isAvailable && driver.isOnline) ||
        (statusFilter === 'offline' && !driver.isOnline);
      const isInactive = driver.isActive === false;
      const activeMatch =
        activeFilter === 'all' ||
        (activeFilter === 'active' && !isInactive) ||
        (activeFilter === 'inactive' && isInactive);
      return driver.isDriver === true && searchMatch && statusMatch && activeMatch;
    }).sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'rides':
          return (b.total_rides || 0) - (a.total_rides || 0);
        case 'status':
          return (STATUS_PRIORITY[b.status] ?? 0) - (STATUS_PRIORITY[a.status] ?? 0);
        default:
          return 0;
      }
    });
  }, [drivers, searchTerm, statusFilter, activeFilter, sortBy]);

  // Pagination – neu berechnet nur wenn filteredData, page oder rowsPerPage sich ändern
  const paginatedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );
  
  if (isLoading) {
    return <Loading message="Fahrer werden geladen..." />;
  }
  
  if (error) {
    console.error('Fehler in DriversList Component:', error);
    return (
      <ErrorMessage
        title="Fehler beim Laden der Fahrer"
        message={error.message || error.details || JSON.stringify(error)}
        // AppDataManager versucht automatisch erneut - kein manueller Retry nötig
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Fahrerverwaltung"
        description="Übersicht und Verwaltung aller Fahrer"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrer', path: '/fahrer' }
        ]}
        actionButton={{
          label: 'Fahrer hinzufügen',
          icon: <AddIcon />,
          path: '/fahrer/neu'
        }}
      />
      
      {/* Filter und Suche */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Suche nach Name oder Telefon"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="available">Verfügbar</MenuItem>
                <MenuItem value="busy">Besetzt</MenuItem>
                <MenuItem value="offline">Nicht verfügbar</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="active-filter-label">Kontostatus</InputLabel>
              <Select
                labelId="active-filter-label"
                id="active-filter"
                value={activeFilter}
                label="Kontostatus"
                onChange={handleActiveFilterChange}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="active">Nur aktive</MenuItem>
                <MenuItem value="inactive">Nur deaktivierte</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="sort-by-label">Sortieren nach</InputLabel>
              <Select
                labelId="sort-by-label"
                id="sort-by"
                value={sortBy}
                label="Sortieren nach"
                onChange={handleSortByChange}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="rating">Bewertung</MenuItem>
                <MenuItem value="rides">Anzahl der Fahrten</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {filteredData.length} Fahrer gefunden
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Fahrertabelle */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="Fahrertabelle">
          <TableHead>
            <TableRow>
              <TableCell>Fahrer</TableCell>
              <TableCell align="center">Online</TableCell>
              <TableCell align="center">Verfügbar</TableCell>
              <TableCell align="center">Bewertung</TableCell>
              <TableCell align="center">Fahrten</TableCell>
              <TableCell align="center">Telefon</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((driver) => (
                <StyledTableRow key={driver.id}>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={driver.profilePic || ''} sx={{ opacity: driver.isActive === false ? 0.5 : 1 }}>
                        {driver.name ? driver.name.charAt(0) : 'F'}
                      </Avatar>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">{driver.name}</Typography>
                          {driver.isActive === false && (
                            <Chip
                              label="Deaktiviert"
                              size="small"
                              icon={<BlockIcon />}
                              color="error"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="textSecondary">{driver.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={driver.isOnline ? "Online" : "Offline"}
                      color={driver.isOnline ? "success" : "default"}
                      size="small"
                      icon={driver.isOnline ? <AvailableIcon /> : <OfflineIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip 
                      label={driver.isOnline ? (driver.isAvailable ? 'Verfügbar' : 'Besetzt') : 'Offline'}
                      status={driver.isOnline ? (driver.isAvailable ? 'available' : 'busy') : 'offline'}
                      size="small"
                      icon={driver.isOnline ? (driver.isAvailable ? <AvailableIcon /> : <BusyIcon />) : <OfflineIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <RatingBox>
                      <StarIcon />
                      <Typography>{driver.rating || 'N/A'}</Typography>
                    </RatingBox>
                  </TableCell>
                  <TableCell align="center">{driver.total_rides || 0}</TableCell>
                  <TableCell align="center">{driver.phone}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`driver-menu-${driver.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, driver)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Keine Fahrer gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}–${to} von ${count}`
          }
        />
      </TableContainer>
      
      {/* Aktions-Menü */}
      <Menu
        id={menuDriver ? `driver-menu-${menuDriver.id}` : undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          component={Link} 
          to={menuDriver ? `/fahrer/${menuDriver.id}` : '#'} 
          onClick={handleCloseMenu}
        >
          Fahrer anzeigen
        </MenuItem>
        <MenuItem 
          component={Link} 
          to={menuDriver ? `/fahrer/${menuDriver.id}/bearbeiten` : '#'} 
          onClick={handleCloseMenu}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        <MenuItem
          onClick={() => menuDriver && handleToggleActive(menuDriver)}
          sx={{ color: menuDriver?.isActive === false ? 'success.main' : 'warning.main' }}
        >
          {menuDriver?.isActive === false ? (
            <><ActivateIcon fontSize="small" sx={{ mr: 1 }} />Aktivieren</>
          ) : (
            <><BlockIcon fontSize="small" sx={{ mr: 1 }} />Deaktivieren</>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => menuDriver && handleDeleteClick(menuDriver)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>
      
      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Fahrer löschen"
        content={`Möchten Sie den Fahrer ${selectedDriver?.user?.name || ''} wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default DriversList;